import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { AlertCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditItemSize,
  getIsItemSizeModalOpen,
  toggleItemSizeModal,
} from "../../features/itemSizes/itemSizesSlice";
import { IItemSizeFormInput } from "../../types/itemSize";
import { t } from "../../locales";
import { formatSize, SIZE_UNIT } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const modalInputClass = (hasError: boolean) =>
  `h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const modalTextareaClass = (hasError: boolean) =>
  `text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

type Props = {
  handleSaveItemSize: (data: IItemSizeFormInput) => void;
};

const ItemSizeFormModal = ({ handleSaveItemSize }: Props) => {
  const dispatch = useAppDispatch();
  const editItemSize = useAppSelector(getEditItemSize);
  const isItemSizeModalOpen = useAppSelector(getIsItemSizeModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IItemSizeFormInput>({
    defaultValues: { size: "", description: "" },
  });

  const handleOnSubmit: SubmitHandler<IItemSizeFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveItemSize(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleItemSizeModal(false));
  };

  useEffect(() => {
    if (editItemSize && editItemSize.id) {
      reset({
        size: String(editItemSize.size),
        description: editItemSize.description,
      });
    } else {
      reset({ size: "", description: "" });
    }
  }, [editItemSize, isItemSizeModalOpen]);

  return (
    <Dialog
      open={isItemSizeModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.itemSizes.formTitle}
          </DialogTitle>
          <DialogDescription>
            {editItemSize ? `Editing: ${formatSize(editItemSize.size)}` : t.itemSizes.addNewItemSize}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="size" className="text-sm font-semibold text-[#113F67]">{t.itemSizes.sizeLabel} ({SIZE_UNIT}) <span className="text-red-500">*</span></Label>
            <Input
              id="size"
              type="text"
              inputMode="decimal"
              placeholder={t.itemSizes.sizePlaceholder}
              {...register("size", {
                required: t.itemSizes.sizeRequired,
                pattern: {
                  value: /^\d+(\.\d)?$/,
                  message: t.itemSizes.sizePatternError,
                },
              })}
              onKeyDown={(e) => {
                // Allow: backspace, delete, tab, escape, enter, decimal point
                if (
                  ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)
                ) {
                  return;
                }
                // Allow decimal point only if not already present
                if (e.key === ".") {
                  if ((e.target as HTMLInputElement).value.includes(".")) {
                    e.preventDefault();
                  }
                  return;
                }
                // Block non-numeric
                if (!/^\d$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              className={modalInputClass(!!errors.size)}
            />
            {errors.size && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{errors.size.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-[#113F67]">{t.itemSizes.descriptionLabel}</Label>
            <Textarea
              id="description"
              rows={3}
              placeholder={t.itemSizes.descriptionPlaceholder}
              {...register("description", { required: false })}
              className={modalTextareaClass(false)}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.itemSizes.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.itemSizes.saving : t.itemSizes.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ItemSizeFormModal;
